/* eslint-disable complexity, max-statements, max-lines */
/********************************/
/*          About vuex          */
/********************************/
// The MIT License (MIT)
//
// Copyright (c) 2015-present Evan You
//
// Permission is hereby granted, free of charge, to any person obtaining a copy
// of this software and associated documentation files (the "Software"), to deal
// in the Software without restriction, including without limitation the rights
// to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
// copies of the Software, and to permit persons to whom the Software is
// furnished to do so, subject to the following conditions:
//
// The above copyright notice and this permission notice shall be included in
// all copies or substantial portions of the Software.
//
// THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
// IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
// FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
// AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
// LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
// OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN
// THE SOFTWARE.

import { mapActions, mapState } from "vuex";
import session from "../../../../../share/session";
import individualDiagnose from "../../../../../share/util/individualDiagnose";
import cstmTable from "../../../../vue/individualActiveTest/activeTestControlPanel/ControlPanelTable";
import atUtil from "../../../../pages/individualActiveTest/individualActiveTestUtil";
import loading from "../../../../../share/components/vue/ProcessingIcon";
import { REQUEST_STATUS_COMPLETED } from "../../../../../share/util/api/polling";
import config from "../../../../../share/config";
import {
  API_TYPE,
  ERR_KEY,
  FLOW,
  FLOW_ERROR,
  MNTR_STATUS,
  RES_TYPE,
  POLLING_RESULT_CONTINUE,
  POLLING_RESULT_FINISH,
  POLLING_RESULT_ERROR,
  AT_EXE_TYPE
} from "../../../../pages/individualActiveTest/individualActiveTestConstants.js";

const PARAM = {
  TITLE: "menu_title",
  USER_DATA: "user_data",
  MNTR_STATUS: "monitoring_status",
  RES_TYPE: "res_type",
  VAL: "value",
  UNIT: "unit",
  EXE_MNTR_CONTINUE: "execution_monitoring_continuate_condition",
  EXE_MNTR_TERMINATE: "execution_monitoring_terminate_condition",
  REQUEST_RESULT: "request_result",
};

const MENUS = {
  ATID: "active_test_id",
  REQID: "requirement_id",
  CTRL_STT: "control_state",
  VAL: "value",
  FLG: "flg",
  RID: "rid",
};

export default {
  name: "requirement30",
  data() {
    return {
      /** 状態遷移 */
      flow: FLOW.INIT,
      columns: [],
      // 変換情報
      conversionInfoList: [],
      items: [],
      /** AT文言 */
      message: "",
      /** 停止中フラグ */
      isStopping: false,
      displayProcessing: false,
      drvBtnDisabled: true,
      // サインアウト
      signout: false,
      currentStatus: '',
    };
  },

  props: {
    // 選択されたAT項目
    selectedAtFunc: { type: Object, required: true },
    // 選択項目名称i18nキー
    i18nMenuTitleKey: { type: String, default: "", required: false },
    // 駆動中フラグ、親画面の画面遷移判定で使用
    isDriving: { type: Boolean, required: true },
    // DM駆動中フラグ
    inExecution: { type: Boolean, required: true },
  },

  /** processing-view, cstm-table */
  components: {
    "processing-view": loading,
    "cstm-table": cstmTable,
  },

  watch: {
    language: {
      handler() {
        // 言語選択時、ヘッダーと計測項目一覧を更新
        this.setColumns();
        this.setItems();
        if (this.currentStatus) {
          this.message = atUtil.getAtMsg(
            this,
            this.systemId,
            this.requirementId,
            parseInt(this.currentStatus, 16),
            this.isDriveFlow
          );
        }
      },
    },
  },

  computed: {
    ...mapState({
      language: (state) => state.systems.language,
      netAppId: (state) => state.netAppConnector.pairingAppId,
      individualMenus: state => state.vehicle.individualMenus,
    }),
    systemId() {
      return individualDiagnose.getCurrentSystemId(this);
    },
    activeTestId() {
      return this.selectedAtFunc[MENUS.ATID];
    },
    requirementId() {
      return this.selectedAtFunc[MENUS.REQID];
    },
    ridList() {
      return this.selectedAtFunc[MENUS.RID];
    },
    controlStateList() {
      return this.selectedAtFunc[MENUS.CTRL_STT];
    },
    uiid() {
      return this.selectedAtFunc[MENUS.ATID].slice(0, -2);
    },
    isDriveFlow() {
      return [
        FLOW.EXECUTE,
        FLOW.CHECK_EXE,
      ].includes(this.flow);
    },
    driveBtnLabel() {
      return !this.isDriveFlow
        ? this.$t("individual.button_active_test_drive")
        : this.$t("individual.button_data_monitor_stop");
    },

    btnDisabled() {
      const disabled = { ac: false };
      return disabled;
    },
  },

  methods: {
    ...mapActions("auth", ["logout", "resetSession"]),

    /**
     * AT項目選択ボタンクリック時の処理
     * AT項目選択 → DM項目選択 遷移時に行う処理を記述する
     */
    async btnAtSelectOnClicked() {
      this.displayProcessing = true;
      this.drvBtnDisabled = true;
      this.flow = FLOW.INIT;
      await this.getConversion();
      this.displayProcessing = false;
    },

    /** 変換情報を取得および初期表示データ設定 */
    async getConversion() {
      try {
        const self = this;
        const conversionRes = await atUtil.getATConversion(self, [self.activeTestId]);
        // eslint-disable-next-line
        if (!conversionRes) throw new Error('Unexpected error occurred');
        this.conversionInfoList = conversionRes.data.conversion_list;
        this.setItems();
      } catch (error) {
        console.error("GetConversion Error:", error);
        // 共通エラーへ遷移
        this.flow = FLOW.ERR;
        this.doErrorProcess(ERR_KEY.COMMON);
        this.clearAtMsg();
        this.$emit("toggle-drive", false);
      }
    },

    /** テーブル表示用データ作成（初期値 = 最小値） */
    setItems() {
      const self = this;
      const items = [];
      self.flow = FLOW.STANDBY;
      self.drvBtnDisabled = false;

      this.ridList.forEach((rid) => {
        // 対象変換情報取得
        const filteredConversionInfoList = self.conversionInfoList.filter(item => {
          return item.rid.slice(0, 4) === rid;
        });
        if (filteredConversionInfoList.length === 0) return;
        filteredConversionInfoList.forEach(conversionInfo => {
          if (conversionInfo.info.data_type.toLowerCase() === MENUS.VAL) {
            let digitsAfterDecimal = 0;
            if (parseFloat(conversionInfo.info.operations_rules.step) < 1) {
              // stepが1より小さい時は、10分の1の位まで表示
              digitsAfterDecimal = 1;
            }
            items.push({
              id: conversionInfo.rid,
              menu_title: self.getRIDI18nName(conversionInfo.rid),
              value: conversionInfo.info.operations_rules.min, // toFixed()によって文字列となるためここではparseFloatしない
              unit: self.$t(`didUnit.` + conversionInfo.info.unit),
              data_type: MENUS.VAL,
              scaling_bit: conversionInfo.info.scaling_bit,
              step: parseFloat(conversionInfo.info.operations_rules.step),
              min: parseFloat(conversionInfo.info.operations_rules.min),
              max: parseFloat(conversionInfo.info.operations_rules.max),
              digits_after_decimal: digitsAfterDecimal
            });
          } else if (conversionInfo.info.data_type.toLowerCase() === MENUS.FLG) {
            items.push({
              id: conversionInfo.rid,
              menu_title: self.getRIDI18nName(conversionInfo.rid),
              value: self.getRIDI18nName(conversionInfo.rid + '_00'),
              unit: self.$t(`didUnit.` + conversionInfo.info.unit),
              data_type: MENUS.FLG,
              step: null,
              min: self.getRIDI18nName(conversionInfo.rid + '_00'),
              max: self.getRIDI18nName(conversionInfo.rid + '_FF'),
            });
          }
        });
      });
      // itemsに要素が存在しなければ、共通エラー
      if (items.length === 0) {
        // 共通エラーへ遷移
        this.flow = FLOW.ERR;
        this.doErrorProcess(ERR_KEY.COMMON);
        this.clearAtMsg();
        this.$emit("toggle-drive", false);
        return;
      }
      this.items = items;
    },

    /**
     * (左右ボタン押下後)項目の更新
     * @param {Object} data 値を変更した項目の情報
     * @param {Number} increase 増減（1 or -1）
     * @param {Number} rate 増減倍率
     */
    updateItem(data, increase = 1, rate = 1) {
      const self = this;
      const itemIndex = self.items.findIndex(item => item.id === data.id);
      if (data.data_type === MENUS.VAL) {
        const differential = increase * rate * data.step;
        const tmpValue = parseFloat(data.value) + differential;
        if (differential < 0) {
          // 減少の場合
          tmpValue < data.min
            ? self.items[itemIndex].value = data.min.toFixed(data.digits_after_decimal)
            : self.items[itemIndex].value = tmpValue.toFixed(data.digits_after_decimal);
        } else {
          // 増加の場合
          tmpValue > data.max
            ? self.items[itemIndex].value = data.max.toFixed(data.digits_after_decimal)
            : self.items[itemIndex].value = tmpValue.toFixed(data.digits_after_decimal);
        }
      } else if (data.data_type === MENUS.FLG) {
        if (increase < 0) {
          // 減少の場合
          self.items[itemIndex].value = self.getRIDI18nName(data.id + '_00');
        } else {
          // 増加の場合
          self.items[itemIndex].value = self.getRIDI18nName(data.id + '_FF');
        }
      }
    },

    /** AT文言の削除 */
    clearAtMsg() {
      this.message = "";
    },

    /** 左矢印ボタン押下時の処理 */
    handleLeftBtnClick(data) {
      if (!this.isDriveFlow) {
        this.updateItem(data, -1, 1);
      }
    },

    /** 右矢印ボタン押下時の処理 */
    handleRightBtnClick(data) {
      if (!this.isDriveFlow) {
        this.updateItem(data, 1, 1);
      }
    },

    /** 左矢印ボタン長押し時の処理 */
    handleLeftBtnHold(data) {
      if (!this.isDriveFlow) {
        this.updateItem(data, -1, 10);
      }
    },

    /** 右矢印ボタン長押し時の処理 */
    handleRightBtnHold(data) {
      if (!this.isDriveFlow) {
        this.updateItem(data, 1, 10);
      }
    },

    /** 駆動/停止ボタン押下時の処理 */
    driveBtn() {
      if (!this.isDriving) {
        // 駆動
        this.startDrive();
      } else {
        // 停止
        this.stopDrive();
      }
    },

    /** 駆動ボタン押下処理 */
    async startDrive() {
      this.$emit("toggle-drive", true);
      this.flow = FLOW.EXECUTE;
      this.clearAtMsg();

      // マーカ登録処理(駆動開始時)
      const infos = [];
      this.items.forEach((item) => {
        const info = {
          "param": item.menu_title,
          "value": item.value
        };
        infos.push(info);
      });
      const markInfo = {
        "type": AT_EXE_TYPE.START,
        "info": infos
      };
      await this.$emit("save-active-test-executeMark", markInfo);

      if (await this.doStart()) {
        if (!this.isStopping) {
          const response = await this.doStop();
          if (response && response.data.status === REQUEST_STATUS_COMPLETED) {
            const result = await this.doTerminate();
            if (result) {
              this.$emit("toggle-drive", false);
              this.flow = FLOW.STANDBY;
            } else {
              // 共通エラーへ遷移
              this.flow = FLOW.ERR;
              this.commonError();
            }
          }
        }
      }
    },

    /** 停止ボタン押下処理 */
    async stopDrive() {
      this.flow = FLOW.STOP;

      // マーカ登録処理(駆動停止時)
      const markInfo = {
        "type": AT_EXE_TYPE.END
      };
      await this.$emit("save-active-test-executeMark", markInfo);

      const response = await this.doStop();
      if (response) {
        if (response.data.status === REQUEST_STATUS_COMPLETED) {
          const result = await this.doTerminate();
          if (result) {
            this.$emit("toggle-drive", false);
            this.flow = FLOW.STANDBY;
            // アクティブ文言のクリア判定
            if (atUtil.isClearMessageOnStop(this.requirementId)) {
              this.clearAtMsg();
            }
          } else {
            // 共通エラーへ遷移
            this.flow = FLOW.ERR;
            this.commonError();
          }
        }
      }
    },

    /******************** 以下API ********************/

    /**
     * 駆動API通信開始
     */
    async doStart() {
      try {
        this.isStopping = false;
        // API実行パラメータ作成
        const token = session.getToken();
        const requestBody = this.genReqBodyStart(token.id_token);
        // start API実行
        const response = await atUtil.pollingAtApi(
          this,
          this.netAppId,
          this.systemId,
          this.activeTestId,
          requestBody,
          // エラー発生のコールバック
          (errorType) => { errorType; atUtil.showUnexpectedError(this); },
          // POST API 結果が異常の場合（handler.validate でエラーが発生した場合）のコールバック
          (result) => { atUtil.validateErrorResult(this, result); },
          // GET API 結果が異常の場合（handler.validate でエラーが発生した場合）のコールバック
          (result) => { atUtil.validateErrorResult(this, result); },
          // GET API レスポンスチェックがin_progress時のコールバック
          () => {
            this.flow = FLOW.EXECUTE;
            return POLLING_RESULT_CONTINUE;
          },
          // GET API レスポンスチェックがcompleted時のコールバック
          (getApiResponse, self) => {
            if (atUtil.isErrorAbort(getApiResponse.data[PARAM.REQUEST_RESULT])) {
              // 開始のポーリングでCCエラー中断のレスポンスは、データモニタでエラーとなった場合のため、通信エラーへ遷移
              // ただしデータモニタ起点で既にエラー処理をおこなっている場合は、そのままポーリング終了
              if (!FLOW_ERROR.includes(this.flow)) {
                this.flow = FLOW.ERR;
                this.doErrorProcess(ERR_KEY.COMMUNICATION);
              }
              return config.INDIVIDUAL_POLLING_RESULT_FINISH;
            }

            const monitoringStatusData = atUtil.getMonitoringStatusData(getApiResponse.data[PARAM.USER_DATA]);
            let result = POLLING_RESULT_ERROR;
            const isExeOnly =
              getApiResponse.data[PARAM.USER_DATA].length === 0 &&
              getApiResponse.data[PARAM.MNTR_STATUS] === MNTR_STATUS.COMPLETE;
            if (
              isExeOnly ||
              !(PARAM.MNTR_STATUS in getApiResponse.data) ||
              !getApiResponse.data[PARAM.MNTR_STATUS]
            ) {
              // 実行
              if (isExeOnly || monitoringStatusData[PARAM.RES_TYPE] === RES_TYPE.OK) {
                // 正常終了
                if (self.isStopping) {
                  // 停止処理が開始されたのでflowは遷移させない
                } else {
                  // 実行監視に遷移
                  self.flow = FLOW.CHECK_EXE;
                  result = POLLING_RESULT_CONTINUE;
                }
              } else {
                // 異常終了 : 通信エラーへ遷移
                self.flow = FLOW.ERR_COMMUNICATE;
                self.doErrorProcess(ERR_KEY.COMMUNICATION);
                result = POLLING_RESULT_FINISH;
              }
            } else {
              // 実行監視
              if (monitoringStatusData[PARAM.RES_TYPE] === RES_TYPE.OK) {
                if (!self.isStopping) {
                  self.flow = FLOW.CHECK_EXE;
                }
                // 正常終了
                this.currentStatus = atUtil.getMonitoringStatus(monitoringStatusData[PARAM.VAL], self.requirementId);
                if (
                  self.selectedAtFunc[PARAM.EXE_MNTR_CONTINUE] &&
                  self.selectedAtFunc[PARAM.EXE_MNTR_CONTINUE].includes(
                    this.currentStatus
                  )
                ) {
                  // 実行監視継続条件
                  self.message = atUtil.getAtMsg(
                    self,
                    self.systemId,
                    self.requirementId,
                    parseInt(this.currentStatus, 16),
                    self.isDriveFlow
                  );
                  result = POLLING_RESULT_CONTINUE;
                } else if (
                  self.selectedAtFunc[PARAM.EXE_MNTR_TERMINATE] &&
                  self.selectedAtFunc[PARAM.EXE_MNTR_TERMINATE].includes(this.currentStatus)
                ) {
                  // 実行監視終了条件
                  this.flow = FLOW.STANDBY;
                  self.message = atUtil.getAtMsg(
                    self,
                    self.systemId,
                    self.requirementId,
                    parseInt(this.currentStatus, 16),
                    self.isDriveFlow
                  );
                  result = POLLING_RESULT_FINISH;
                } else {
                  // それ以外 : 通信エラーへ遷移
                  self.flow = FLOW.ERR_COMMUNICATE;
                  self.doErrorProcess(ERR_KEY.COMMUNICATION);
                  result = POLLING_RESULT_FINISH;
                }
              } else {
                // 異常終了 : 通信エラーへ遷移
                self.flow = FLOW.ERR_COMMUNICATE;
                self.doErrorProcess(ERR_KEY.COMMUNICATION);
                result = POLLING_RESULT_FINISH;
              }
            }
            if (self.isStopping) {
              // 停止処理が開始されたのでstartのポーリングを終了させる
              result = POLLING_RESULT_FINISH;
            }
            return result;
          },
          // GET API レスポンスチェックがfailed時のコールバック
          (getApiResponse, self) => {
            // 共通エラーへ遷移
            self.flow = FLOW.ERR;
            self.doErrorProcess(ERR_KEY.COMMON);
          },
          () => { return ""; },
          Number.MAX_SAFE_INTEGER
        );
        return response;
      } catch {
        // 例外 : 共通エラーへ遷移
        this.flow = FLOW.ERR;
        this.doErrorProcess(ERR_KEY.COMMON);
        return null;
      }
    },

    /**
     * 駆動API通信停止
     */
    async doStop() {
      try {
        this.isStopping = true;
        const self = this;

        // API実行パラメータ作成
        const requestBody = {
          id_token: session.getToken(),
          rid: self.items[0].id.slice(0, 4),
          type: API_TYPE.STOP
        };

        // stop API実行
        const response = await atUtil.pollingAtApi(
          self,
          self.netAppId,
          self.systemId,
          self.activeTestId,
          requestBody,
          // エラー発生のコールバック
          (errorType) => { errorType; atUtil.showUnexpectedError(self); },
          // POST API 結果が異常の場合（handler.validate でエラーが発生した場合）のコールバック
          (result) => { atUtil.validateErrorResult(self, result); },
          // GET API 結果が異常の場合（handler.validate でエラーが発生した場合）のコールバック
          (result) => { atUtil.validateErrorResult(self, result); },
          // GET API レスポンスチェックがin_progress時のコールバック
          () => { return POLLING_RESULT_CONTINUE; },
          // GET API レスポンスチェックがcompleted時のコールバック
          (getApiResponse, self) => {
            const monitoringStatusData = atUtil.getMonitoringStatusData(getApiResponse.data[PARAM.USER_DATA]);
            let result = POLLING_RESULT_ERROR;
            const isStopOnly =
              getApiResponse.data[PARAM.USER_DATA].length === 0 &&
              getApiResponse.data[PARAM.MNTR_STATUS] === MNTR_STATUS.COMPLETE;
            if (
              isStopOnly ||
              !(PARAM.MNTR_STATUS in getApiResponse.data) ||
              !getApiResponse.data[PARAM.MNTR_STATUS]
            ) {
              // 停止
              if (
                isStopOnly ||
                monitoringStatusData[PARAM.RES_TYPE] === RES_TYPE.OK
              ) {
                // 正常終了
                result = POLLING_RESULT_FINISH;
              } else {
                // 異常終了 : 共通エラーへ遷移
                self.flow = FLOW.ERR;
                self.doErrorProcess(ERR_KEY.COMMON);
                result = POLLING_RESULT_FINISH;
              }
            } else {
              // 停止監視 : 共通エラーへ遷移
              self.flow = FLOW.ERR;
              self.doErrorProcess(ERR_KEY.COMMON);
              result = POLLING_RESULT_FINISH;
            }
            return result;
          },
          // GET API レスポンスチェックがfailed時のコールバック
          (getApiResponse, self) => {
            getApiResponse;
            // 共通エラーへ遷移
            self.flow = FLOW.ERR;
            self.doErrorProcess(ERR_KEY.COMMON);
          }
        );
        return response;
      } catch {
        // 共通エラーへ遷移
        this.flow = FLOW.ERR;
        this.doErrorProcess(ERR_KEY.COMMON);
        return null;
      }
    },

    /** 正常時のAT終了処理 
     * @async
     * @return {boolean} terminate result - false is failed.
     */
    async doTerminate() {
      const response = await atUtil.doTerminate(this, false);
      if (response) {
        if (response.data.status === REQUEST_STATUS_COMPLETED) {
          // 正常終了
          if (!response.data.user_data
            || response.data.user_data.length === 0
            || response.data.user_data[0][PARAM.RES_TYPE] === RES_TYPE.OK) {

            return true;
          }
        }
      }

      return false;
    },

    /******************** エラー処理 ********************/

    /** 
     * エラー処理の振り分け
     * @param {*} errKey エラーキー
     */
    async doErrorProcess(errKey) {
      switch (errKey) {
        case ERR_KEY.COMMUNICATION:
          await this.communicateError(errKey);
          break;
        case ERR_KEY.COMMON:
        default:
          this.commonError();
          break;
      }
    },

    /** 通信エラー画面処理 */
    async communicateError(errKey) {
      this.isStopping = true;
      this.displayProcessing = true;
      const response = await this.doStop();
      this.displayProcessing = false;
      if (response && response.data.status === REQUEST_STATUS_COMPLETED) {
        let checkResponse = true;
        if (response.data.user_data && Array.isArray(response.data.user_data)) {
          for (const userData of response.data.user_data) {
            if (userData.res_type !== RES_TYPE.OK) {
              checkResponse = false;
              break;
            }
          }
        }
        // 共通エラー
        if (!checkResponse) {
          // 共通エラーへ遷移
          this.flow = FLOW.ERR;
          this.commonError();
          return;
        }
      }
      // エラー
      atUtil.showUnexpectedError(
        this,
        errKey,
        this.requirementId,
        null,
        async () => {
          this.displayProcessing = true;
          await atUtil.doTerminate(this, true);
          this.displayInit();
          this.$emit('at-failure');
          this.displayProcessing = false;
        }
      );
      return;
    },

    /** 共通エラー画面処理 */
    commonError() {
      // 共通エラーを表示
      atUtil.showUnexpectedError(
        this,
        ERR_KEY.COMMON,
        this.requirementId,
        null, // 共通エラーはnull
        async () => {
          this.displayProcessing = true;
          await atUtil.doTerminate(this, true);
          // 停止時に他の操作をさせないようにする
          this.displayInit();
          this.$emit('at-failure');
          this.displayProcessing = false;
        }
      );
    },

    /******************** その他処理 ********************/

    /** RIDに対応する翻訳を取得 */
    getRIDI18nName(rid) {
      const key = `atDid${this.systemId}.${rid}`;
      return this.$te(key) ? this.$t(key) : "";
    },

    /** startAPIリクエストボディの生成 */
    genReqBodyStart(idToken) {
      const self = this;
      // byteでソート
      const sortedItems = self.items.sort((a, b) => {
        // id = 'XXXX_X(1桁)_all' を想定
        return parseInt(a.id.slice(5, 6)) - parseInt(b.id.slice(5, 6));
      });
      let record = '';
      if (sortedItems[0].data_type === MENUS.VAL) {
        sortedItems.forEach(item => {
          record = record.concat(
            Math.round(parseFloat(item.value) / item.scaling_bit)
              .toString(16)
              .padStart(2, '0')
              .toUpperCase()
          );
        });
      } else if (sortedItems[0].data_type === MENUS.FLG) {
        sortedItems.forEach(item => {
          if (item.value === self.getRIDI18nName(item.id + '_00')) {
            record = record.concat("00");
          } else {
            record = record.concat("FF");
          }
        });
      }
      return {
        id_token: idToken,
        rid: self.items[0].id.slice(0, 4),
        routine_control_option_record: record,
        type: API_TYPE.START
      };
    },

    /** 初期化 */
    displayInit() {
      // 初期化
      this.flow = FLOW.INIT;
      this.$emit("toggle-drive", false);
    },

    /** 実行中か否かを返す */
    checkDriving() {
      return this.isDriving;
    },

    /** テーブルカラムの設定 */
    setColumns() {
      const columns = [
        { name: PARAM.TITLE, label: this.$t("individual.header_work_support_table_item"), useHtmlBind: true },
        { name: PARAM.VAL, label: this.$t("individual.header_customize_write_settings"), useHtmlBind: true, settingButton: true },
        { name: PARAM.UNIT, label: this.$t("individual.label_detail_unit"), useHtmlBind: true },
      ];
      this.columns = columns;
    },

    /** 「戻る」ボタン押下時処理 */
    async cancelExec() {
      this.currentExecuteTime = null;
      // 駆動中であれば停止する。ただし他のエラー処理を既に実行していれば何もしない
      if (this.isDriveFlow && !FLOW_ERROR.includes(this.flow)) {
        this.flow = FLOW.STOP;
        await this.doStop();
      }
    },

    /** signout */
    setSignout() {
      this.signout = true;
    },

  },

  mounted() {
    this.drvBtnDisabled = true;
    this.setColumns();
  },
};
